import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Star,
  CheckCircle,
  ExternalLink,
  Loader2,
  AlertCircle,
  Truck,
  Calendar,
  MapPin,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RatingInput = ({ label, value, onChange }) => {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star
              className={`w-8 h-8 ${
                star <= value
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300 hover:text-yellow-200"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export default function SurveyPublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [surveyInfo, setSurveyInfo] = useState(null);

  // Form state
  const [ratings, setRatings] = useState({
    ponctualite: 0,
    service: 0,
    qualite: 0,
    hygiene: 0,
    prix: 0,
  });
  const [commentaire, setCommentaire] = useState("");
  const [googleClicked, setGoogleClicked] = useState(false);

  useEffect(() => {
    loadSurvey();
  }, [token]);

  const loadSurvey = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/survey/${token}`);
      
      if (response.data.status === "already_completed") {
        setSubmitted(true);
        setSurveyInfo({ alreadyCompleted: true, message: response.data.message });
      } else {
        setSurveyInfo(response.data);
      }
    } catch (err) {
      console.error("Error loading survey:", err);
      setError("Sondage introuvable ou expiré");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all ratings
    const missingRatings = Object.entries(ratings)
      .filter(([_, value]) => value === 0)
      .map(([key]) => key);

    if (missingRatings.length > 0) {
      toast.error("Veuillez donner une note pour tous les critères");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/survey/${token}`, {
        ...ratings,
        commentaire,
        google_review_clicked: googleClicked,
      });

      setSubmitted(true);
      toast.success("Merci pour votre avis !");
    } catch (err) {
      console.error("Error submitting:", err);
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleReviewClick = () => {
    setGoogleClicked(true);
    if (surveyInfo?.google_review_url) {
      window.open(surveyInfo.google_review_url, "_blank");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Oops !</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Merci !</h2>
            <p className="text-gray-600 mb-6">
              {surveyInfo?.alreadyCompleted 
                ? surveyInfo.message
                : "Votre avis a bien été enregistré. Il nous aide à améliorer nos services !"}
            </p>
            
            {/* Google Review CTA (only if not already completed) */}
            {!surveyInfo?.alreadyCompleted && surveyInfo?.include_google_review && surveyInfo?.google_review_url && !googleClicked && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-3">
                  Vous avez apprécié notre service ? Partagez votre expérience sur Google !
                </p>
                <Button
                  onClick={handleGoogleReviewClick}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Laisser un avis Google
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Votre avis compte !
          </h1>
          <p className="text-gray-600">
            Aidez-nous à améliorer nos services en répondant à ce court sondage
          </p>
        </div>

        {/* Event & Food Truck Info */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {surveyInfo?.food_truck?.logo_url && (
                <img
                  src={surveyInfo.food_truck.logo_url}
                  alt="Logo"
                  className="w-24 h-24 object-cover rounded-lg"
                />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Truck className="w-5 h-5 text-orange-500" />
                  <h2 className="text-xl font-bold text-gray-800">
                    {surveyInfo?.food_truck?.nom_food_truck || "Food Truck"}
                  </h2>
                </div>
                {surveyInfo?.event && (
                  <>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span>{surveyInfo.event.titre}</span>
                    </div>
                    {surveyInfo.event.date_debut && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                        <span>
                          {format(parseISO(surveyInfo.event.date_debut), "d MMMM yyyy", { locale: fr })}
                        </span>
                      </div>
                    )}
                    {surveyInfo.event.lieu && (
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <MapPin className="w-4 h-4" />
                        <span>{surveyInfo.event.lieu}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rating Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Évaluez votre expérience</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <RatingInput
                  label="Ponctualité"
                  value={ratings.ponctualite}
                  onChange={(v) => setRatings({ ...ratings, ponctualite: v })}
                />
                <RatingInput
                  label="Qualité du service"
                  value={ratings.service}
                  onChange={(v) => setRatings({ ...ratings, service: v })}
                />
                <RatingInput
                  label="Qualité des produits"
                  value={ratings.qualite}
                  onChange={(v) => setRatings({ ...ratings, qualite: v })}
                />
                <RatingInput
                  label="Hygiène"
                  value={ratings.hygiene}
                  onChange={(v) => setRatings({ ...ratings, hygiene: v })}
                />
                <RatingInput
                  label="Rapport qualité/prix"
                  value={ratings.prix}
                  onChange={(v) => setRatings({ ...ratings, prix: v })}
                />
              </div>

              <div>
                <Label htmlFor="commentaire">
                  Commentaire (optionnel)
                </Label>
                <Textarea
                  id="commentaire"
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  placeholder="Partagez votre expérience..."
                  rows={4}
                  className="mt-2"
                />
              </div>

              {/* Google Review Option */}
              {surveyInfo?.include_google_review && surveyInfo?.google_review_url && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 mb-3">
                    Vous pouvez également nous laisser un avis sur Google pour partager votre expérience avec d'autres clients !
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleReviewClick}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Ouvrir Google Avis
                  </Button>
                  {googleClicked && (
                    <span className="ml-2 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      Merci !
                    </span>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  "Envoyer mon avis"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Ce sondage est confidentiel et anonyme.
        </p>
      </div>
    </div>
  );
}
